import { ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { AppService, CoreProxyService, CoreTypes, IncidentTypes, LabelValue } from 'common-cloudstudio';
import { IncidentService } from '../../../services/incident.service';
import { Subscription } from 'rxjs';

export interface ITS_Dispatches extends CoreTypes.ITS_IncidentAssistanceDispatch { 
  TransferEndDateTime?: CoreTypes.DateTimeDT;
  Identification?: string;
  Observation?: string;
}


@Component({
  selector: 'app-incident-assistances',
  templateUrl: './incident-assistances.component.html',
  styleUrls: ['./incident-assistances.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IncidentAssistencesComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => IncidentAssistencesComponent),
      multi: true,
    }
  ]
})
export class IncidentAssistencesComponent implements OnInit, OnDestroy, ControlValueAccessor {
  _data: ITS_Dispatches = new CoreTypes.ITS_IncidentAssistanceDispatch();

  @Input('data')
  set data(data: string) {
    this.writeValue(data);
  }
  get data() {
    return JSON.stringify(this._data);
  }
  @Input() editing: boolean = false;
  @Input() incidentID: number = 0;
  onChange = (_:any) => { }
  onTouch = () => { }

  @Output() disableForm = new EventEmitter<boolean>();
  _errorMessage = "";

  today = new Date();
  minDateDispatch = new Date()
  minDateDeparture = new Date();
  minDateArrival = new Date();
  minDateEndDispatch = new Date();
  minDateEndAssistance = new Date();

  isDisabled: boolean;
  subscriptions: Subscription[] = [];
  _assetsFullList: CoreTypes.Asset[] = [];
  _resourceTypeList: LabelValue<number>[] = [{label: 'Propio', value:1 }, { label: 'Externo', value:2 }];
  _movilList: LabelValue<number>[] = [];
  _externalMovilList: LabelValue<number>[] = [];
  _assistanceList: LabelValue<number>[] = [];
  _dispatchErrorMessage: string;
  _externalMovilFullList: CoreTypes.ITS_ExternalAssistanceServices[] = [];
  incidentElementTypes = IncidentTypes.elementTypes;

  form: UntypedFormGroup = this.fb.group({
    resourceType: [this._resourceTypeList[0], [Validators.required]],
    movil: [new LabelValue<number>()],
    externalMovil: [new LabelValue<number>()],
    dispatchLicensePlate: [''],
    dispatchDateTime: [new CoreTypes.DateTimeDT()],
    departureDateTime: [new CoreTypes.DateTimeDT()],
    arrivalDateTime: [new CoreTypes.DateTimeDT()],
    dateEndDateTimeDispacth: [new CoreTypes.DateTimeDT()],
    dateEndDateTimeTransfer: [new CoreTypes.DateTimeDT()],
    assistance: [[new LabelValue<number>()], this.incidentID ? [ Validators.required] : []],
    observation: [''],
    identification: [''],
    dispatchReference: [''],
  });

  constructor(    
    private fb: UntypedFormBuilder,
    public appService: AppService,
    private coreProxyService: CoreProxyService,
    private incidentService: IncidentService,
    private cdRef:ChangeDetectorRef
  ) {}
  
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public validate() {
    let errors: ValidationErrors = {};
    Object.keys(this.form.controls).forEach(key => {
      const controlErrors: ValidationErrors = this.form.get(key).errors;
      if (controlErrors != null) {
        errors[key] = controlErrors;
      }
    });
    return errors;
  }

  ngOnInit(): void {
    this.getAssets();
    this.getITSExternalAssistanceServices();
    this.getITSAssistanceTypes();
    this.subscriptions.push(this.resourceType.valueChanges.subscribe(value => {
      if(value?.value == 1) {
        this._data.ServiceID = null;
      } else if(value?.value == 2) {
        this._data.VehicleID = null;
      }
      this.onInput();

    }));
    this.subscriptions.push(this.movil.valueChanges.subscribe(value => {
      if(value?.value || value?.value === 0) {
        this._data.VehicleID = value.value;
        this.onInput();
      }
    }));
    this.subscriptions.push(this.externalMovil.valueChanges.subscribe(value => {
      if(value?.value || value?.value === 0) {
        this._data.ServiceID = value.value;
        this.onInput();
      }
    }));
    
    this.subscriptions.push(this.dispatchLicensePlate.valueChanges.subscribe(value => {
      if(value) {
        this._data.LicensePlate = value;
        this.onInput();
      }
    }));

    this.subscriptions.push(this.assistance.valueChanges.subscribe(value => {
      if(value?.value) {
        this._data.AssistanceType = value.value;
        this.onInput();
      }
    }));

    this.subscriptions.push(this.observation.valueChanges.subscribe(value => {
      if(value) {
        this._data.Observation = value;
        this.onInput();
      }
    }));
    
    this.subscriptions.push(this.identification.valueChanges.subscribe(value => {
      if(value) {
        this._data.Identification = value;
        this.onInput();
      }
    }));

    this.subscriptions.push(this.dispatchReference.valueChanges.subscribe(value => {
      if(value) {
        this._data.Reference = value;
        this.onInput();
      }
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onInput() {
    ((this.resourceType?.value?.value == 1 && !this.movil?.value?.value) || (this.resourceType?.value?.value == 2 && !this.externalMovil?.value?.value)) ? this.disableForm.emit(true) : this.disableForm.emit(false);
    this.onTouch();
    this.onChange(JSON.stringify(this._data));
  }

  writeValue(value: any): void {
    if (value) {
      this._data = JSON.parse(value);
      this.dispatchLicensePlate.setValue(this._data?.LicensePlate);
      this.dispatchReference.setValue(this._data.Reference);
      this.observation.setValue(this._data.Observation);
      this.identification.setValue(this._data.Identification);
    } else {
      this._data = new CoreTypes.ITS_IncidentAssistanceDispatch();
    }
    this._data.DispatchDateTime?.Date?.Day ? this.dispatchDateTime.setValue(this._data.DispatchDateTime) : this.dispatchDateTime.setValue(null);
    this._data.DepartureDateTime?.Date?.Day ? this.departureDateTime.setValue(this._data.DepartureDateTime) : this.departureDateTime.setValue(null);
    this._data.ArrivalDateTime?.Date?.Day ? this.arrivalDateTime.setValue(this._data.ArrivalDateTime) : this.arrivalDateTime.setValue(null);
    this._data.EndDateTime?.Date?.Day ? this.dateEndDateTimeDispacth.setValue(this._data.EndDateTime) : this.dateEndDateTimeDispacth.setValue(null);
    this._data.TransferEndDateTime?.Date?.Day ? this.dateEndDateTimeTransfer.setValue(this._data.TransferEndDateTime) : this.dateEndDateTimeTransfer.setValue(null);
    this.updateMinDates();

    let movil;
    if(this._data?.VehicleID) {
      this.resourceType.setValue(this._resourceTypeList[0]);
      movil = this._movilList.find(m => m.value == this._data?.VehicleID) ?? this._movilList[0];
      this.movil.setValue(movil);
    } else if(this._data?.ServiceID) {
      this.resourceType.setValue(this._resourceTypeList[1]);
      movil = this._externalMovilList.find(m => m.value == this._data.ServiceID) ?? this._externalMovilList[0];
      this.externalMovil.setValue(movil);
    } else {
      this.resourceType.setValue(this._resourceTypeList[0]);
      this.movil.setValue(this._movilList[0]);
    }
    let assistance = this._assistanceList.find(a => a.value === this._data?.AssistanceType)?? this._assistanceList[0];
    this.assistance.setValue(assistance);
    this.onInput();
  }

  disabledItem(element: any): boolean {
    return this.incidentService.isItemSelectedInSection(this.incidentElementTypes.Dispatches, element);
  }

  async getITSExternalAssistanceServices() {
    this._externalMovilList = [];
    this._externalMovilFullList = [];
    this._externalMovilList = await this.incidentService.getITSExternalAssistanceServices();
    this._externalMovilFullList = await this.incidentService._externalServiceList.value;
    let optionsList: LabelValue<number>[] = [];
    if(this._externalMovilFullList.length) {
      optionsList.push({
        label: this.appService.CoreCommonLiterals.None_Male,
        value: 0
      });
    }
    this._externalMovilFullList.forEach((x) => {
      let item = { label: x.ServiceName, value: x.ServiceID, disabled: this.disabledItem(x.ServiceID) };
      optionsList.push(item);
    });
    this._externalMovilList = optionsList;
    let externalMovil = this._externalMovilList.find(m => m.value == this._data?.ServiceID) ?? this._externalMovilList[0];
    this.externalMovil.setValue(externalMovil);
  }

  async getAssets() {
    if(this.resourceType?.value?.value !== 1) return;
    this._assetsFullList = [];
    this._movilList = [];
    let optionsList: LabelValue<number>[] = [];
    await this.incidentService.getAssetsList();
    this._assetsFullList = await this.incidentService._assetsList.value;
    if(this._assetsFullList.length) {
      optionsList.push({
        label: this.appService.CoreCommonLiterals.None_Male,
        value: 0
      });
    }
    this._assetsFullList.forEach(x => {
      let item = { label: x.DisplayDescription, value: x.AssetID, disabled: this.disabledItem(x.AssetID) };
      optionsList.push(item);
    });
    this._movilList = optionsList;
    let movil = this._movilList.find(m => m.value == this._data?.VehicleID) ?? this._movilList[0];
    this.movil.setValue(movil);
    this.dispatchLicensePlate.setValue(this._assetsFullList.find(x => x.AssetID == this.movil?.value.value)?.AssetVehicleData?.LicensePlateNumber?? this._data.LicensePlate);
  }

  async onSelectResource(resourceType,) {
    if(resourceType.value?.value === 1) {
      this.getAssets();
    } else if(resourceType.value?.value === 2) {
      this.getITSExternalAssistanceServices();
      this.dispatchLicensePlate.setValue('');
    }
  }

    onSelectMovil(movil) {
    this.dispatchLicensePlate.setValue(this._assetsFullList.find(x => x.AssetID == movil?.value)?.AssetVehicleData?.LicensePlateNumber?? "");
  }

  async getITSAssistanceTypes() {
    this._assistanceList = [];
    this.incidentService.getAssistenceTypes().then(async assistence => {
      this._assistanceList = await this.incidentService._assistenceTypeList.value;
      let assistance = this._assistanceList.find(a => a.value === this._data.AssistanceType)?? this._assistanceList[0];
      this.assistance.setValue(assistance);
      this.cdRef.detectChanges();
    });
  }
  
  updateMinDates() {
    this.today = new Date();
    if(this.dispatchDateTime?.value?.Date?.Day) { this.minDateDispatch = this.incidentService.setMinDate(this.dispatchDateTime?.value); }
    this.departureDateTime?.value?.Date?.Day ? this.minDateDeparture = this.incidentService.setMinDate(this.departureDateTime?.value) : this.minDateDeparture = this.minDateDispatch;
    if(this.arrivalDateTime?.value?.Date?.Day) { this.minDateEndDispatch = this.incidentService.setMinDate(this.arrivalDateTime?.value); }
    if(this.dateEndDateTimeDispacth.value?.Date?.Day) { this.minDateEndAssistance = this.incidentService.setMinDate(this.dateEndDateTimeDispacth?.value); }
    if(!this.dateEndDateTimeDispacth?.value?.Date?.Day && !this.arrivalDateTime?.value?.Date?.Day && this._dispatchErrorMessage) {
      this._dispatchErrorMessage = ""
    } 
  }

  applyFilters(controlName:string) {
    this.updateMinDates();
    switch (controlName) {
      case 'departureDateTime':
        this._data.DepartureDateTime = this.departureDateTime.value;
        this.cdRef.detectChanges();
        break;
      case 'dispatchDateTime':
        this._data.DispatchDateTime = this.dispatchDateTime.value; 
        break;
      case 'arrivalDateTime':
        this._data.ArrivalDateTime = this.arrivalDateTime.value;
        this.cdRef.detectChanges();
        break;
      case 'dateEndDateTimeDispacth':
        this._data.EndDateTime = this.dateEndDateTimeDispacth.value;
        this.cdRef.detectChanges();
        break;
      case 'dateEndDateTimeTransfer':
        this._data.TransferEndDateTime = this.dateEndDateTimeTransfer.value;
        break;
      default:
        break;
    }
    setTimeout(() => this.onInput(), 100); 
  }

  updateMaxDate() {
    this.today = new Date();
  }

  get resourceType() { return this.form.get('resourceType'); }
  get movil() { return this.form.get('movil'); }
  get externalMovil() { return this.form.get('externalMovil'); }
  get dispatchLicensePlate() { return this.form.get('dispatchLicensePlate'); }
  get dispatchDateTime() { return this.form.get('dispatchDateTime'); }
  get departureDateTime() { return this.form.get('departureDateTime'); }
  get arrivalDateTime() { return this.form.get('arrivalDateTime'); }
  get dateEndDateTimeDispacth() { return this.form.get('dateEndDateTimeDispacth'); }
  get dateEndDateTimeTransfer() { return this.form.get('dateEndDateTimeTransfer'); }
  get assistance() { return this.form.get('assistance'); }
  get observation() { return this.form.get('observation'); }
  get identification() { return this.form.get('identification'); }
  get dispatchReference() { return this.form.get('dispatchReference'); }
}